import React, { useMemo, useState } from "react"
import { Button, Modal, notification } from "antd"
import { useDispatch } from "react-redux"
import { Icon } from "components"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import { setLoader, setOrders as setOrdersAction } from "store/actions"
import ChangeStatusButton from "./ChangeStatusButton"
import OrdersStatusMap from "./OrdersStatusMap"
import BulkClassificationActions from "./BulkClassificationActions"

// Type assertion helper for dynamic strings
const OrderStrings = Strings.orders as any

interface MultiOrderPanelProps {
	selectedOrders: any[]
	totalOrders: number
	allOrders: any[]
	setOrders: React.Dispatch<React.SetStateAction<any[]>>
	onClose: () => void
	onChangeLot: (orderIds: string[]) => void
	onRemoveOrder: (orderId: string) => void
	onSelectOrders: (orderIds: string[]) => void
	deliveryZones?: any[]
	filterZone?: { geojson?: any } | null
	waitForModalConfirmation: (nextStatus: string) => Promise<boolean> // Function to show confirmation modal and wait for user response
}

const MultiOrderPanel: React.FC<MultiOrderPanelProps> = ({
	selectedOrders,
	totalOrders,
	allOrders,
	setOrders,
	onClose,
	onChangeLot,
	onRemoveOrder,
	onSelectOrders,
	deliveryZones,
	filterZone,
	waitForModalConfirmation,
}) => {
	const dispatch = useDispatch()
	const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)

	// Check if all selected orders have the same status
	const allSameStatus = useMemo(() => {
		if (selectedOrders.length === 0) return false
		const firstStatus = selectedOrders[0].status
		return selectedOrders.every((order) => order.status === firstStatus)
	}, [selectedOrders])

	// Check if all selected orders are in address_confirmation status
	const allInAddressConfirmation = useMemo(() => {
		if (selectedOrders.length === 0) return false
		return selectedOrders.every(
			(order) => order.orderFlow?.status === "address_confirmation"
		)
	}, [selectedOrders])

	const selectedOrderIds = selectedOrders.map((order) => order._id)

	const ordersWithLabel = useMemo(
		() => selectedOrders.filter((order) => !!order.label),
		[selectedOrders]
	)

	const handleDownloadLabels = () => {
		ordersWithLabel.forEach((order) => {
			const iframe = document.createElement("iframe")
			iframe.style.display = "none"
			iframe.src = order.label.url
			document.body.appendChild(iframe)
			setTimeout(() => {
				document.body.removeChild(iframe)
			}, 60000)
		})
	}

	const handleDeleteOrders = async () => {
		dispatch(setLoader(true))

		try {
			const response = await API.delete({
				url: Endpoints.uriOrders(),
				data: { orderIds: selectedOrderIds },
			})

			if (response.ok) {
				const filteredOrders = allOrders.filter(
					(order) => !selectedOrderIds.includes(order._id)
				)
				dispatch(setOrdersAction(filteredOrders))
				onClose()

				notification.success({
					message: Strings.sidebar.orders,
					description: response?.data?.message,
					placement: "bottomRight",
					duration: 5,
				})
			} else {
				notification.error({
					message: Strings.sidebar.orders,
					description: response.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (error) {
			notification.error({
				message: Strings.sidebar.orders,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			dispatch(setLoader(false))
			setShowDeleteConfirmModal(false)
		}
	}

	return (
		<div className="OrderDetailPanel">
			<div className="OrderDetailPanel__header">
				<div className="OrderDetailPanel__title">
					<h3>
						{Strings.orders.ordersSelectedOf
							.replace("{0}", String(selectedOrders.length))
							.replace("{1}", String(totalOrders))}
					</h3>
				</div>
				<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
					<Button type="text" icon={<Icon name="close" />} onClick={onClose} />
				</div>
			</div>

			<div className="OrderDetailPanel__content">
				{/* Show ChangeStatusButton - it handles the same-status validation internally */}
				<ChangeStatusButton
					orders={selectedOrders}
					setOrders={setOrders}
					waitForModalConfirmation={waitForModalConfirmation}
				/>
				{!allSameStatus && selectedOrders.length > 1 && (
					<p style={{ color: "#999", fontSize: 13, marginBottom: 16 }}>
						{OrderStrings.allOrdersSameStatus}
					</p>
				)}

				<div className="OrderDetailPanel__actions">
					<div className="OrderDetailPanel__actions-header">
						<Icon name="lightning" />
						{Strings.generic.actions}
					</div>
					<div className="OrderDetailPanel__actions-grid">
						<div
							className={`OrderDetailPanel__actions-item${ordersWithLabel.length === 0 ? " OrderDetailPanel__actions-item--disabled" : ""}`}
							onClick={
								ordersWithLabel.length > 0 ? handleDownloadLabels : undefined
							}
						>
							<Icon name="tag" />
							<span>
								{Strings.orders.downloadLabel}
								{ordersWithLabel.length > 0 && ` (${ordersWithLabel.length})`}
							</span>
						</div>
						<div
							className={`OrderDetailPanel__actions-item${!allInAddressConfirmation ? " OrderDetailPanel__actions-item--disabled" : ""}`}
							onClick={
								allInAddressConfirmation
									? () => onChangeLot(selectedOrderIds)
									: undefined
							}
						>
							<Icon name="package" />
							<span>{Strings.orders.changeLot}</span>
						</div>
						<BulkClassificationActions
							selectedOrders={selectedOrders}
							setOrders={setOrders}
							onSelectOrders={onSelectOrders}
						/>
						<div
							className={`OrderDetailPanel__actions-item OrderDetailPanel__actions-item--danger${!allInAddressConfirmation ? " OrderDetailPanel__actions-item--disabled" : ""}`}
							onClick={
								allInAddressConfirmation
									? () => setShowDeleteConfirmModal(true)
									: undefined
							}
						>
							<Icon name="trash" />
							<span>
								{Strings.orders.deleteOrders.replace(
									"{0}",
									String(selectedOrders.length)
								)}
							</span>
						</div>
					</div>
					{!allInAddressConfirmation && (
						<p className="OrderDetailPanel__actions-hint">
							{Strings.orders.actionsOnlyAddressConfirmation}
						</p>
					)}
				</div>

				{/* Map showing selected orders with status-colored markers */}
				<OrdersStatusMap
					orders={selectedOrders}
					onRemoveOrder={onRemoveOrder}
					deliveryZones={deliveryZones}
					filterZone={filterZone}
				/>
			</div>

			<Modal
				title={
					<div style={{ display: "flex", alignItems: "center" }}>
						<Icon
							name="warning"
							style={{ color: "#ff4d4f", marginRight: 8, fontSize: 20 }}
						/>
						<span>{Strings.orders.confirmDeleteTitle}</span>
					</div>
				}
				visible={showDeleteConfirmModal}
				onCancel={() => setShowDeleteConfirmModal(false)}
				footer={[
					<Button key="cancel" onClick={() => setShowDeleteConfirmModal(false)}>
						{Strings.generic.cancel}
					</Button>,
					<Button
						key="delete"
						type="primary"
						danger
						onClick={handleDeleteOrders}
					>
						{Strings.orders.deleteOrders.replace(
							"{0}",
							String(selectedOrders.length)
						)}
					</Button>,
				]}
			>
				<div>
					<div
						style={{
							padding: "12px",
							backgroundColor: "#fff1f0",
							border: "1px solid #ffccc7",
							borderRadius: "4px",
							marginBottom: "16px",
						}}
					>
						<div
							style={{
								fontWeight: "bold",
								color: "#cf1322",
								marginBottom: "8px",
							}}
						>
							<span role="img" aria-label="Warning">
								⚠️
							</span>{" "}
							{Strings.orders.confirmDeleteWarning}
						</div>
						<div style={{ color: "#8c8c8c", fontSize: "14px" }}>
							{Strings.orders.confirmDeleteBody.replace(
								"{0}",
								String(selectedOrders.length)
							)}
						</div>
					</div>

					<div style={{ marginBottom: "12px" }}>
						<strong>{Strings.orders.ordersToDelete}</strong>
					</div>
					<div
						style={{
							maxHeight: "200px",
							overflowY: "auto",
							padding: "8px",
							backgroundColor: "#fafafa",
							border: "1px solid #d9d9d9",
							borderRadius: "4px",
						}}
					>
						{selectedOrders.map((order) => (
							<div
								key={order._id}
								style={{
									padding: "4px 8px",
									marginBottom: "4px",
									backgroundColor: "#fff",
									border: "1px solid #e8e8e8",
									borderRadius: "2px",
									fontSize: "13px",
								}}
							>
								{order.orderId || order._id}
							</div>
						))}
					</div>
				</div>
			</Modal>
		</div>
	)
}

export default MultiOrderPanel
